import React, { useEffect, useMemo, useState } from 'react';
import { apiRequest } from '../../services/apiClient';
import { useMultiFetch } from '../../hooks/useMultiFetch';
import DirectorPanelState from './DirectorPanelState';
import {
  DIRECTOR_DATA_UPDATED_EVENT,
  notifyDirectorAdminToast,
  notifyDirectorDataUpdated
} from '../../utils/directorEvents';
import { formatRoleLabel } from '../../config/appRoutes';
import pilotDataPolicy from '../../shared/pilotDataPolicy.json';

const ALL_DIRECTOR_LOCATIONS = 'ALL';

function parseCsv(text) {
  const lines = String(text || '')
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean);

  if (lines.length < 2) {
    return [];
  }

  const headers = lines[0].split(',').map(value => value.trim());
  return lines.slice(1).map((line) => {
    const values = line.split(',').map(value => value.trim());
    return headers.reduce((accumulator, header, index) => {
      accumulator[header] = values[index] ?? '';
      return accumulator;
    }, {});
  });
}

function roleLabel(roleKey) {
  return formatRoleLabel(roleKey || 'ops_manager');
}

function DirectorManagerRosterImportCard({
  selectedLocationCode = ALL_DIRECTOR_LOCATIONS,
  locationLabel = 'Company Rollup'
}) {
  const [isUploadingRoster, setIsUploadingRoster] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [uploadSummary, setUploadSummary] = useState(null);
  const [provisionedAccessItems, setProvisionedAccessItems] = useState([]);

  const { data: fetchedRoster, isLoading, error: fetchError, refetch } = useMultiFetch({
    locations: '/api/locations',
    managers: '/api/managers',
  });

  useEffect(() => {
    const handler = () => refetch();
    window.addEventListener(DIRECTOR_DATA_UPDATED_EVENT, handler);
    return () => window.removeEventListener(DIRECTOR_DATA_UPDATED_EVENT, handler);
  }, [refetch]);

  const locations = useMemo(
    () => (Array.isArray(fetchedRoster.locations) ? fetchedRoster.locations : []),
    [fetchedRoster.locations]
  );
  const managers = useMemo(
    () => (Array.isArray(fetchedRoster.managers) ? fetchedRoster.managers : []),
    [fetchedRoster.managers]
  );

  useEffect(() => {
    if (fetchError) {
      setError(fetchError);
    }
  }, [fetchError]);

  const scopedManagers = useMemo(
    () => (selectedLocationCode !== ALL_DIRECTOR_LOCATIONS
      ? managers.filter(manager => manager.locationCode === selectedLocationCode)
      : managers),
    [managers, selectedLocationCode]
  );


  const handleRosterUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setMessage('');
    setError('');
    setUploadSummary(null);
    setProvisionedAccessItems([]);
    setIsUploadingRoster(true);

    try {
      const text = await file.text();
      const rows = parseCsv(text);
      if (!rows.length) {
        throw new Error('CSV must include a header row and at least one leadership row.');
      }
      const prohibitedHeaders = Object.keys(rows[0]).filter(header => {
        const normalized = String(header || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
        return pilotDataPolicy.prohibitedDataKeys.includes(normalized);
      });
      if (prohibitedHeaders.length) {
        throw new Error(`CSV contains fields that are not permitted during the client pilot: ${prohibitedHeaders.join(', ')}.`);
      }

      const locationMap = new Map(
        locations.map(location => [String(location.code || '').toUpperCase(), location])
      );
      const existingByManagerKey = new Map(
        managers.map(manager => [String(manager.managerKey || '').toLowerCase(), manager])
      );
      const existingByEmployeeId = new Map(
        managers.map(manager => [String(manager.employeeId || '').toLowerCase(), manager])
      );

      const results = [];

      for (let index = 0; index < rows.length; index += 1) {
        const row = rows[index];
        const managerKey = String(row.managerKey || row.manager_key || '').trim();
        const employeeId = String(row.employeeId || row.employee_id || '').trim();
        const firstName = String(row.firstName || row.first_name || '').trim();
        const lastName = String(row.lastName || row.last_name || '').trim();
        const locationCode = String(row.locationCode || row.location || '').trim().toUpperCase();
        const roleTitle = String(row.roleTitle || row.role_title || '').trim();

        try {
          if (!managerKey || !employeeId || !firstName || !lastName || !locationCode || !roleTitle) {
            throw new Error('managerKey, employeeId, firstName, lastName, locationCode, and roleTitle are required.');
          }

          const location = locationMap.get(locationCode);
          if (!location) {
            throw new Error(`Unknown locationCode "${locationCode}".`);
          }

          const existingManager =
            existingByManagerKey.get(managerKey.toLowerCase())
            || existingByEmployeeId.get(employeeId.toLowerCase())
            || null;

          const payload = {
            managerKey,
            employeeId,
            firstName,
            lastName,
            email: String(row.email || '').trim() || null,
            locationId: Number(location.id),
            managerRoleKey: String(row.managerRoleKey || row.manager_role_key || 'ops_manager').trim() || 'ops_manager',
            roleTitle,
            department: String(row.department || '').trim() || null,
            hireDate: String(row.hireDate || row.hire_date || '').trim() || null,
            activeFrom: String(row.activeFrom || row.active_from || '').trim() || new Date().toISOString().slice(0, 10)
          };

          const savedManager = existingManager
            ? await apiRequest(`/api/managers/${existingManager.id}`, {
              method: 'PATCH',
              body: JSON.stringify(payload)
            })
            : await apiRequest('/api/managers', {
              method: 'POST',
              body: JSON.stringify(payload)
            });

          existingByManagerKey.set(managerKey.toLowerCase(), savedManager);
          existingByEmployeeId.set(employeeId.toLowerCase(), savedManager);
          results.push({
            rowNumber: index + 2,
            status: existingManager ? 'updated' : 'created',
            label: `${savedManager.firstName} ${savedManager.lastName}`,
            detail: `${savedManager.locationCode} / ${roleLabel(savedManager.managerRoleKey)}`,
            accessProvisioning: savedManager.accessProvisioning || null
          });
        } catch (rowError) {
          results.push({
            rowNumber: index + 2,
            status: 'failed',
            label: `${firstName || 'Unknown'} ${lastName || 'Manager'}`.trim(),
            detail: rowError.message || 'Unable to process row.'
          });
        }
      }

      refetch();

      const createdCount = results.filter(result => result.status === 'created').length;
      const updatedCount = results.filter(result => result.status === 'updated').length;
      const failedRows = results.filter(result => result.status === 'failed');
      const provisionedRows = results
        .filter(result => result.accessProvisioning?.user)
        .map(result => ({
          rowNumber: result.rowNumber,
          managerLabel: result.label,
          email: result.accessProvisioning.user.email,
          role: roleLabel(result.accessProvisioning.user.role),
          provisioningMode: result.accessProvisioning.mode,
          temporaryPassword: result.accessProvisioning.temporaryPassword || ''
        }));

      setUploadSummary({
        fileName: file.name,
        createdCount,
        updatedCount,
        failedRows,
        provisionedRows
      });
      setProvisionedAccessItems(provisionedRows);
      setMessage(`Leadership directory upload complete. ${createdCount} created, ${updatedCount} updated, ${failedRows.length} failed.`);
      notifyDirectorAdminToast(`Leadership directory upload complete. ${createdCount} created, ${updatedCount} updated, ${failedRows.length} failed.`, failedRows.length ? 'warning' : 'success');
      notifyDirectorDataUpdated('Director dashboard refreshed for leadership directory upload.');
    } catch (requestError) {
      notifyDirectorAdminToast(requestError.message || 'Unable to process leadership directory CSV.', 'error');
      setError(requestError.message || 'Unable to process leadership directory CSV.');
    } finally {
      setIsUploadingRoster(false);
      event.target.value = '';
    }
  };

  return (
    <section className="director-card">
      <div className="role-section-header">
        <h2>Import Leadership Directory CSV</h2>
        <span>{selectedLocationCode === ALL_DIRECTOR_LOCATIONS ? 'multi-site' : locationLabel}</span>
      </div>
      <p className="role-section-copy">
        Upload a leadership CSV to create new manager-backed leadership records or update existing rows by <code>managerKey</code> or <code>employeeId</code> within the current scope.
      </p>
      <label className="director-upload-card">
        <span>{isUploadingRoster ? 'Uploading Leadership Directory CSV...' : 'Choose Leadership Directory CSV'}</span>
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={handleRosterUpload}
          disabled={isUploadingRoster || isLoading}
        />
      </label>
      <p className="director-muted">
        Columns: <code>managerKey, employeeId, firstName, lastName, email, locationCode, managerRoleKey, roleTitle, department, hireDate, activeFrom</code>
      </p>

      <DirectorPanelState kind="loading" message={isLoading ? 'Loading leadership directory import context...' : ''} />
      <DirectorPanelState kind="success" message={!error ? message : ''} />
      <DirectorPanelState kind="error" message={error} />

      {provisionedAccessItems.length ? (
        <div className="director-history-list">
          <div className="director-history-item">
            <div className="director-history-row">
              <strong>New Access Credentials</strong>
              <span>{provisionedAccessItems.filter(item => item.temporaryPassword).length} temporary password{provisionedAccessItems.filter(item => item.temporaryPassword).length === 1 ? '' : 's'}</span>
            </div>
            <div className="director-muted">
              Temporary passwords are shown once. Share them securely and require the user to change password on first login.
            </div>
          </div>
          {provisionedAccessItems.map(item => (
            <div className="director-history-item" key={`${item.rowNumber}-${item.email}`}>
              <div className="director-history-row">
                <strong>{item.managerLabel}</strong>
                <span>{item.role}</span>
              </div>
              <div className="director-muted">{item.email}</div>
              <div className="director-muted">
                {item.provisioningMode === 'created'
                  ? `Temporary password: ${item.temporaryPassword}`
                  : item.provisioningMode === 'linked_existing'
                    ? 'Existing access was linked automatically.'
                    : item.provisioningMode === 'updated_existing'
                      ? 'Existing access was updated to match the latest manager email and role.'
                      : 'Access was already linked.'}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {uploadSummary ? (
        <div className="director-history-list">
          <div className="director-history-item">
            <div className="director-history-row">
              <strong>{uploadSummary.fileName}</strong>
              <span>{uploadSummary.createdCount} created / {uploadSummary.updatedCount} updated</span>
            </div>
            <div className="director-muted">
              {uploadSummary.failedRows.length
                ? `${uploadSummary.failedRows.length} rows failed. Review the details below and correct the CSV before retrying.`
                : uploadSummary.provisionedRows?.length
                  ? `All rows were processed successfully. ${uploadSummary.provisionedRows.length} access record${uploadSummary.provisionedRows.length === 1 ? '' : 's'} were provisioned or linked automatically.`
                  : 'All rows were processed successfully.'}
            </div>
          </div>
          {uploadSummary.failedRows.slice(0, 8).map(row => (
            <div className="director-history-item" key={`${row.rowNumber}-${row.label}`}>
              <div className="director-history-row">
                <strong>Row {row.rowNumber}</strong>
                <span>{row.label}</span>
              </div>
              <div className="director-muted">{row.detail}</div>
            </div>
          ))}
        </div>
      ) : null}

      <p className="director-muted">
        {scopedManagers.length} leadership record{scopedManagers.length === 1 ? '' : 's'} are currently visible in this import scope.
      </p>
    </section>
  );
}

export default DirectorManagerRosterImportCard;
